import React, { useState } from "react";
import {
  FaTruckPickup,
  FaCar,
  FaShuttleVan,
  FaTruck,
  FaSprayCan,
  FaCrown,
  FaHandSparkles,
} from "react-icons/fa";
import { FaTruckMonster } from "react-icons/fa6";

interface VehicleType {
  name: "Utilitario" | "Auto" | "SUV" | "Pickup" | "4x4 Alto Rendimiento";
  icon: React.ElementType;
}

interface PricingPlan {
  title: string;
  benefits: string[];
  prices: {
    [key in VehicleType["name"]]: number;
  };
  icon: React.ElementType;
}

const vehicleTypes: VehicleType[] = [
  { name: "Utilitario", icon: FaTruck },
  { name: "Auto", icon: FaCar },
  { name: "SUV", icon: FaShuttleVan },
  { name: "Pickup", icon: FaTruckPickup },
  { name: "4x4 Alto Rendimiento", icon: FaTruckMonster },
];

const plans: PricingPlan[] = [
  {
    title: "Lavado Glowin Shine",
    prices: {
      Utilitario: 60000,
      Auto: 60000,
      SUV: 75000,
      Pickup: 80000,
      "4x4 Alto Rendimiento": 110000,
    },
    icon: FaHandSparkles,
    benefits: [
      "Lavado de llantas y buche de rueda",
      "Manoplas de lavado manual",
      "Aspirado de interior",
      "Secado con paños de microfibra",
    ],
  },
  {
    title: "Lavado Glowin Detail",
    prices: {
      Utilitario: 90000,
      Auto: 100000,
      SUV: 120000,
      Pickup: 130000,
      "4x4 Alto Rendimiento": 250000,
    },
    icon: FaSprayCan,
    benefits: [
      "Descontaminación de llantas",
      "Lavado full con foam lance",
      "Shampoo de alta densidad",
      "Protección de plásticos",
      "Acondicionamiento de cueros",
      "Aspirado interior y baúl",
      "Limpieza de vidrios",
    ],
  },
  {
    title: "Lavado Glowin Luxury",
    prices: {
      Utilitario: 150000,
      Auto: 160000,
      SUV: 170000,
      Pickup: 180000,
      "4x4 Alto Rendimiento": 280000,
    },
    icon: FaCrown,
    benefits: [
      "Descontaminación de llantas",
      "Pre-lavado con clay-bar",
      "Lavado full con foam lance",
      "Protección de plásticos",
      "Renovación de cueros",
      "Eliminación de olores",
      "Abrillantado de carrocería",
      "Sellado hidrofóbico",
    ],
  },
];

const PricingPlans: React.FC = () => {
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleType>(
    vehicleTypes[0]
  );

  const showDimensionsNotice = ["Utilitario", "SUV", "Pickup"].includes(
    selectedVehicle.name
  );

  return (
    <section
      id="pricing-plans"
      className="container mx-auto px-4 py-12 text-white"
    >
      <h2 className="text-4xl font-bold text-center mb-8">
        Nuestros Servicios
      </h2>

      {/* Tab Selector */}
      <div className="grid grid-cols-3 gap-6 mb-12 sm:grid-cols-3 sm:gap-6 md:flex md:justify-center md:gap-6">
        {vehicleTypes.map((vehicle, index) => (
          <button
            key={vehicle.name}
            onClick={() => setSelectedVehicle(vehicle)}
            className={`w-28 h-28 flex flex-col items-center justify-center rounded-full transition-colors ${
              selectedVehicle.name === vehicle.name
                ? "bg-yellow-500 text-black"
                : "bg-gray-700 hover:bg-gray-600"
            } ${
              index >= 3
                ? "sm:col-span-1 sm:justify-self-center ml-[60%] sm:ml-auto sm:mr-auto md:ml-0 md:mr-0"
                : ""
            }`}
          >
            <vehicle.icon className="text-3xl mb-2" />
            <span className="text-sm font-medium">{vehicle.name}</span>
          </button>
        ))}
      </div>

      {/* Leyenda Condicional */}
      {showDimensionsNotice && (
        <p className="text-center text-medium text-yellow-500 mb-6">
          El costo inicial depende de las dimensiones del vehículo.
        </p>
      )}

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {plans.map((plan, index) => {
          const whatsappMessage = `Hola! Me contacto para consultarte por el servicio de ${selectedVehicle.name} - ${plan.title}`;
          const whatsappLink = `https://wa.me/541126413277?text=${encodeURIComponent(
            whatsappMessage
          )}`;

          return (
            <div
              key={index}
              className="relative flex flex-col items-center bg-[rgb(25,24,31)] p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
            >
              <plan.icon className="text-yellow-500 text-5xl mb-4" />
              <h3 className="text-2xl font-bold mb-4">{plan.title}</h3>
              <p className="text-4xl font-semibold mb-6">
                ${plan.prices[selectedVehicle.name]}
              </p>
              <ul className="text-lg mb-6 space-y-2">
                {plan.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="mr-2 text-yellow-500">•</span>
                    {benefit}
                  </li>
                ))}
              </ul>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto py-2 px-4 bg-yellow-500 text-black rounded-full font-semibold hover:bg-yellow-400 transition-colors"
              >
                Agendar Servicio
              </a>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PricingPlans;
